import { Facebook, Telegram } from '@mui/icons-material';
import { Box, Button, Link, Stack, Typography, useTheme, alpha } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// TikTok Icon Component
const TikTokIcon = ({ fontSize = 'large', style }) => {
  // Material-UI fontSize mapping: small=1.25rem, medium=1.5rem, large=2.1875rem
  const sizeMap = {
    small: '1.25rem',
    medium: '1.5rem',
    large: '2.1875rem',
    inherit: 'inherit'
  };
  const size = typeof fontSize === 'string' && sizeMap[fontSize] ? sizeMap[fontSize] : fontSize;
  
  return (
    <svg
      viewBox="0 0 24 24"
      style={{
        width: size,
        height: size,
        display: 'inline-block',
        verticalAlign: 'middle',
        ...style
      }}
      fill="currentColor"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

function WelcomePage() {
    const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const descriptionText = t('confirmation.description');
  const welcomeText = t('confirmation.welcome');
  const instructionText = t('confirmation.instruction');
  const getStartedTodayText = t('confirmation.getStartedToday');
  
  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundImage: 'url(/welcome.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: alpha(theme.palette.primary.main, 0.7),
          zIndex: 1,
        }}
      />
      
      {/* Content */}
      <Box
        px={{ xs: 2, md: 5, lg: 10 }}
        py={10}
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
        }}
      >
        <Stack height={'100%'} rowGap={10} justifyContent={'center'} alignItems={'center'}>
          <Stack rowGap={4}>
            <Typography 
              textAlign={'center'} 
              variant="h4" 
              sx={{
                color: 'white',
                fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                fontWeight: 600,
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              }}
            >
              {welcomeText}
            </Typography>
            <Typography 
              textAlign={'center'} 
              sx={{
                color: 'white',
                fontSize: { xs: '1.1rem', md: '1.3rem', lg: '1.5rem' },
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
              }}
            >
              {instructionText}
            </Typography>
            <Typography 
              textAlign={'center'} 
              sx={{
                color: theme.palette.secondary.main,
                fontSize: { xs: '1.1rem', md: '1.3rem', lg: '1.5rem' },
                fontWeight: 600,
                textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
              }}
            >
              {getStartedTodayText}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
            <Button
              sx={{
                background: theme.palette.secondary.main,
                color: theme.palette.primary.main,
                textTransform: 'capitalize',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.1rem',
                boxShadow: `0 8px 30px ${alpha(theme.palette.secondary.main, 0.4)}`,
                '&:hover': {
                  background: alpha(theme.palette.secondary.main, 0.9),
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 40px ${alpha(theme.palette.secondary.main, 0.5)}`,
                },
              }}
              onClick={() => navigate('/login')}
            >
              <Typography fontWeight={600} fontSize={20}>
                {t('confirmation.getStarted')}
              </Typography>
            </Button>
            <Button
              sx={{
                background: 'transparent',
                border: `2px solid white`,
                color: 'white',
                textTransform: 'capitalize',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 700,
                fontSize: '1.1rem',
                '&:hover': {
                  background: alpha('#ffffff', 0.1),
                  borderColor: 'white',
                },
              }}
              onClick={() => navigate('/')}
            >
              <Typography fontWeight={600} fontSize={20}>
                {t('confirmation.home')}
              </Typography>
            </Button>
          </Stack>

          <Stack direction="row" spacing={4}>
            <Link
              href={`https://www.facebook.com/sharer/sharer.php?u=https://www.astegne.com/&quote=${descriptionText}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'white',
                '&:hover': {
                  color: alpha('#ffffff', 0.8),
                },
              }}
            >
              <Facebook fontSize="large" />
            </Link>
            <Link
              href="https://www.tiktok.com/@astegne"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'white',
                '&:hover': {
                  color: alpha('#ffffff', 0.8),
                },
              }}
            >
              <TikTokIcon fontSize="large" />
            </Link>
            <Link
              href={`https://telegram.me/share/url?url=https://www.astegne.com/&text=${descriptionText}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'white',
                '&:hover': {
                  color: alpha('#ffffff', 0.8),
                },
              }}
            >
              <Telegram fontSize="large" />
            </Link>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

export default WelcomePage;
